import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, MoreVertical, Trash2, Pencil, MapPin, Search, RefreshCw, Monitor, RotateCw, Smartphone, Box, Wifi, WifiOff, Globe, ChevronLeft, ChevronRight, Info, X } from 'lucide-react'

import { Card } from '@meno/ui/components/ui/card'
import { Button } from '@meno/ui/components/ui/button'
import { Badge } from '@meno/ui/components/ui/badge'
import { ScreenStatusBadge, screenDisplayStatus } from '@meno/ui/components/shared/ScreenStatusBadge'
import { Input } from '@meno/ui/components/ui/input'
import { Skeleton } from '@meno/ui/components/ui/skeleton'
import { Switch } from '@meno/ui/components/ui/switch'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@meno/ui/components/ui/dropdown-menu'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@meno/ui/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@meno/ui/components/ui/select'

import { getScreens, getScreen, deleteScreen, updateScreen } from '@meno/ui/lib/api'
import { ConfirmDialog } from '@meno/ui/components/ui/confirm-dialog'
import DeleteScreenDialog from '@/components/DeleteScreenDialog'
import DetachScreenDialog from '@/components/DetachScreenDialog'
import { formatRelativeSyncTime } from '@meno/ui/lib/utils'
import { usePageHeader } from '@meno/ui/components/layout/PageHeader'
import { usePermissions } from '@meno/ui/hooks/usePermissions'
import { TabButton } from '@meno/ui/components/shared/TabButton'
import { useBranchFilter } from '@meno/ui/contexts/BranchFilterContext'
import { useScreenStatus } from '@meno/ui/contexts/ScreenStatusContext'
import ScreenGroupsPanel from '@/components/ScreenGroupsPanel'
import ScreensEmptyState from '@/components/ScreensEmptyState'
import ViewToggle, { type ViewMode } from '@/components/ViewToggle'
import ScreenInfoDrawer from '@/components/ScreenInfoDrawer'
import { WEB_PLAYER_URL, WEB_PLAYER_HOST } from '@/lib/webPlayer'

// Screens per page in both the table and grid views.
const SCREENS_PAGE_SIZE = 9

type ItemRef = { id: number; name: string }
type Screen = {
  id: number; public_id: string; name: string; status: string; branch_id?: number
  // Operator-facing status (folds license expiry into connectivity) — served by
  // the backend so a license-ended screen reads "expired", never "online".
  effective_status?: string
  disconnect_at?: string | null
  location?: string; ip_address?: string
  orientation_type: 'horizontal' | 'vertical'
  rotation_degrees: 0 | 90 | 180 | 270
  is_active: boolean; created_at: string; last_seen?: string
  menus: ItemRef[]; visuals: ItemRef[]
  device_kind?: 'android' | 'web'   // backend defaults legacy rows to 'android'
  // Hardware currently bound. Distinct from is_paired — an expired screen is
  // unpaired but still claims its device. Drives Detach vs Delete.
  has_device?: boolean
}

// Status shown/filtered/counted here comes from the shared helper so it stays
// identical to the screen-details, groups, and admin surfaces.
const dispStatus = screenDisplayStatus

// Small chip showing how this screen is being played. Drives no behavior —
// just helps operators distinguish a TV running the APK from one running
// the PWA at web.wetook.net, which matters for support / troubleshooting.
function DeviceKindChip({ kind }: { kind?: 'android' | 'web' }) {
  const { t } = useTranslation()
  const k = kind ?? 'android'
  const isWeb = k === 'web'
  // Brand palette: both kinds sit on the neutral muted chip — the LABEL is
  // the differentiator, not the colour, which keeps the table calm and
  // aligned with the rest of the chrome.
  return (
    <span
      className="inline-flex items-center text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
      title={isWeb
        ? t('screens.kindWebTitle', 'Browser player ({{host}})', { host: WEB_PLAYER_HOST })
        : t('screens.kindAndroidTitle', 'Android player app')}
    >
      {isWeb ? t('screens.kindWeb', 'Web') : t('screens.kindAndroid', 'Android')}
    </span>
  )
}

function ScreenThumbnail({
  orientationType, rotationDegrees,
}: {
  orientationType: 'horizontal' | 'vertical'
  rotationDegrees: number
}) {
  const isVertical = orientationType === 'vertical'
  const iconClass = 'w-4 h-4 text-muted-foreground/60'

  return (
    <div className="w-16 h-12 flex items-center justify-center shrink-0 overflow-hidden">
      <div
        className={`${isVertical ? 'w-7 h-11' : 'w-14 h-9'} rounded-md bg-muted flex items-center justify-center border border-border/60 shadow-sm`}
        style={rotationDegrees ? { transform: `rotate(${rotationDegrees}deg)` } : undefined}
      >
        {isVertical ? <Smartphone className={iconClass} /> : <Monitor className={iconClass} />}
      </div>
    </div>
  )
}

export default function ScreensPage() {
  const { t, i18n } = useTranslation()
  const PrevIcon = ChevronLeft
  const NextIcon = ChevronRight
  // Title + description live in the TopBar (like every other page) instead of
  // an inline page header.
  usePageHeader({
    title: t('screens.title', 'Devices'),
    subtitle: t('screens.subtitle', 'Manage and monitor all your digital signage displays'),
  })
  const navigate = useNavigate()
  const { can } = usePermissions()
  const canCreate = can('screens:create')

  const [tab, setTab] = useState<'screens' | 'groups'>('screens')
  const [view, setView] = useState<ViewMode>('list')
  const [page, setPage] = useState(1)

  // Branches the user can see + which are toggled on in the sidebar filter.
  const { branches, enabledIds, isEnabled } = useBranchFilter()
  const [screens, setScreens] = useState<Screen[]>([])
  const [deleteTarget, setDeleteTarget] = useState<Screen | null>(null)
  const [detachTarget, setDetachTarget] = useState<Screen | null>(null)
  // Screen whose right-side info drawer is open (null = closed).
  const [infoTarget, setInfoTarget] = useState<Screen | null>(null)

  const [syncingId, setSyncingId] = useState<number | null>(null)

  const refreshScreen = async (screen: Screen) => {
    setSyncingId(screen.id)
    try {
      const { data } = await getScreen(screen.public_id)
      setScreens(prev => prev.map(s => s.id === screen.id ? { ...s, ...data } : s))
    } catch (err) {
      console.error('Failed to refresh screen', err)
    } finally {
      setSyncingId(null)
    }
  }

  const [search, setSearch] = useState('')
  const [filterBranch, setFilterBranch] = useState('all')
  const [filterStatus, setFilterStatus] = useState('all')
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await getScreens()
        setScreens(data)
      } catch (err) {
        console.error("Failed to load screen data", err)
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [])

  // Live status overlay — this list previously never refreshed after the
  // initial load (a status flip only showed up via a manual per-row refresh
  // or a full page reload). A screen_status push now updates the relevant
  // row in place, org-wide, without a poll.
  const liveStatus = useScreenStatus()
  useEffect(() => {
    setScreens(prev => prev.map(s => {
      const entry = liveStatus(s.id)
      if (!entry || (entry.status === s.status && entry.effective_status === s.effective_status && (entry.last_seen ?? undefined) === s.last_seen)) return s
      return { ...s, status: entry.status, effective_status: entry.effective_status ?? s.effective_status, last_seen: entry.last_seen ?? s.last_seen }
    }))
  }, [liveStatus])

  const remove = async () => {
    if (!deleteTarget) return
    await deleteScreen(deleteTarget.public_id)
    setScreens(prev => prev.filter(s => s.id !== deleteTarget.id))
    setDeleteTarget(null)
  }

  const toggleActive = async (screen: Screen) => {
    const next = !screen.is_active
    // Optimistic update
    setScreens(prev => prev.map(s => s.id === screen.id ? { ...s, is_active: next } : s))
    try {
      await updateScreen(screen.public_id, { is_active: next })
    } catch (err) {
      console.error(err)
      // Revert on failure
      setScreens(prev => prev.map(s => s.id === screen.id ? { ...s, is_active: screen.is_active } : s))
    }
  }

  // Filter application. `matchEnabled` applies the sidebar branch filter:
  // screens assigned to a toggled-off (or non-accessible) branch are hidden;
  // unassigned screens always pass since the filter only narrows by branch.
  const filtered = screens.filter(s => {
    const matchSearch = s.name.toLowerCase().includes(search.toLowerCase())
    const matchBranch = filterBranch === 'all' || s.branch_id?.toString() === filterBranch
    const matchStatus = filterStatus === 'all' || dispStatus(s) === filterStatus
    const matchEnabled = isEnabled(s.branch_id)
    return matchSearch && matchBranch && matchStatus && matchEnabled
  })

  // Online screens surface first (then idle, error, expired, offline); ties keep
  // the API's original order. Applies to both the table and grid views.
  const statusRank: Record<string, number> = { online: 0, idle: 1, error: 2, expired: 3, offline: 4 }
  const sorted = [...filtered].sort(
    (a, b) => (statusRank[dispStatus(a)] ?? 5) - (statusRank[dispStatus(b)] ?? 5),
  )

  // Paginate both views. Page is clamped so a filter/delete that shrinks the
  // list can't strand the user on an out-of-range page.
  const totalPages = Math.max(1, Math.ceil(filtered.length / SCREENS_PAGE_SIZE))
  const curPage    = Math.min(page, totalPages)
  const paged      = sorted.slice((curPage - 1) * SCREENS_PAGE_SIZE, curPage * SCREENS_PAGE_SIZE)
  // Reset to page 1 whenever the filters narrow the list.
  useEffect(() => { setPage(1) }, [search, filterBranch, filterStatus])

  // Fresh account (no linked screens at all) → show the groups-style empty
  // state instead of an empty table. A non-empty list that a search/branch
  // filter narrows to zero keeps the table with its "no matching" row, so the
  // filters stay usable.
  const noScreens = !isLoading && screens.length === 0

  // Branches visible in the branch selector (respects the sidebar filter).
  const enabledBranches = branches.filter(b => enabledIds.includes(b.id))

  // Summary scope: every screen inside the currently-selected branch (or all
  // enabled branches when 'all'). Deliberately NOT narrowed by the search /
  // status filters — the cards report the branch's true totals, not a
  // search-narrowed slice.
  const scopeScreens = screens.filter(s =>
    isEnabled(s.branch_id) && (filterBranch === 'all' || s.branch_id?.toString() === filterBranch),
  )
  const totalCount   = scopeScreens.length
  const onlineCount  = scopeScreens.filter(s => dispStatus(s) === 'online').length
  // Offline groups genuine disconnects + errors + license-expired screens.
  const offlineCount = scopeScreens.filter(s => ['offline', 'error', 'expired'].includes(dispStatus(s))).length
  const pct = (n: number) => (totalCount ? Math.round((n / totalCount) * 100) : 0)
  const selectedBranchName = filterBranch === 'all'
    ? t('screens.allBranches', 'All Branches')
    : enabledBranches.find(b => b.id.toString() === filterBranch)?.name ?? t('screens.allBranches', 'All Branches')

  return (
    <div className="space-y-6 pb-20">

      {/* ── Tab strip: Screens / Groups — flush under the TopBar, mirrors the
          Content & Audio hubs. ─────────────────────────────────────────── */}
      <div className="-mx-4 sm:-mx-[var(--space-l)] -mt-1 sm:-mt-[calc(var(--space-l)-0.75rem)] mb-0 px-4 sm:px-[var(--space-l)] border-b border-border bg-transparent">
        <div className="flex gap-1">
          <TabButton active={tab === 'screens'} onClick={() => setTab('screens')} icon={Monitor} label={t('screens.tabScreens', 'Screens')} />
          <TabButton active={tab === 'groups'}  onClick={() => setTab('groups')}  icon={Box}     label={t('screens.tabGroups', 'Groups')} />
        </div>
      </div>

      {tab === 'groups' ? (
        <ScreenGroupsPanel allScreens={screens} />
      ) : (
      <div className="relative space-y-6">
      {/* ── Summary cards — Total / Online / Offline + a Branch card whose
          value is a branch selector (the filter). Numbers reflect the
          selected branch scope. ─────────────────────────────────────────── */}
      {/* 2-column grid on phone (no side-scroll — the Branch card, which doubles
          as the filter, stays reachable without swiping), a single row from `sm`
          up: each card keeps a text-safe minimum width and stretches to fill
          when there's room (minmax(…,1fr)). Once the cards can't all fit at
          that minimum — the point where labels would clip — the row scrolls
          sideways instead. The subtle scrollbar shows only while it overflows,
          so the scroll stays discoverable on desktop. `sm:grid-cols-none` drops
          the phone's 2-column template: left in place, the first two cards sat
          in its minmax(0,1fr) columns and shrank to bare icons once the row
          overflowed, while only the rest got the 14rem minimum. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-none sm:grid-flow-col sm:auto-cols-[minmax(14rem,1fr)] sm:overflow-x-auto sm:subtle-scrollbar sm:gap-4">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[92px] rounded-2xl" />)
        ) : (
          <>
            <StatCard
              icon={Monitor}
              tile="bg-muted"
              iconColor="text-foreground"
              label={t('screens.totalScreens', 'Total Screens')}
              value={totalCount}
              hint={filterBranch === 'all'
                ? t('screens.acrossBranches', 'Across your branches')
                : t('screens.inThisBranch', 'In this branch')}
            />
            <StatCard
              icon={Wifi}
              tile="bg-emerald-500/10"
              iconColor="text-emerald-500"
              label={t('screens.onlineScreens', 'Online Screens')}
              value={onlineCount}
              hint={t('dashboard.pctOfTotal', { defaultValue: '{{pct}}% of total', pct: pct(onlineCount) })}
            />
            <StatCard
              icon={WifiOff}
              tile="bg-destructive/10"
              iconColor="text-destructive"
              label={t('screens.offlineScreens', 'Offline Screens')}
              value={offlineCount}
              hint={t('dashboard.pctOfTotal', { defaultValue: '{{pct}}% of total', pct: pct(offlineCount) })}
            />

            {/* Branch card — the value doubles as the branch filter. */}
            <div className="rounded-2xl bg-card p-4 sm:p-5 flex max-sm:flex-col max-sm:items-start items-center gap-3 sm:gap-4">
              <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
              </div>
              <div className="min-w-0 w-full flex-1">
                <p className="text-xs font-medium text-muted-foreground">{t('screens.branch', 'Branch')}</p>
                <Select value={filterBranch} onValueChange={setFilterBranch}>
                  <SelectTrigger
                    aria-label={t('screens.selectBranch', 'Select branch')}
                    className="h-auto min-h-0 border-0 bg-transparent p-0 shadow-none gap-1.5 font-bold text-lg leading-tight focus:ring-0 focus:ring-offset-0 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:opacity-70 truncate"
                  >
                    <SelectValue>{selectedBranchName}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('screens.allBranches', 'All Branches')}</SelectItem>
                    {enabledBranches.map(b => (
                      <SelectItem key={b.id} value={b.id.toString()}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-0.5">{t('screens.filterByBranch', 'Filter screens by branch')}</p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Advanced Filters Bar ─────────────────────────────── */}
      <div className="flex flex-col sm:flex-row items-center gap-3 bg-muted/30 p-3 rounded-xl">
        <div className="relative w-full sm:max-w-xs flex-1">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t('common.search', 'Search devices...')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full ps-9 pe-9 bg-background h-10"
          />
          {/* Clear — one click back to the full list instead of hunting for
              backspace on a TV-room keyboard. */}
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label={t('common.clearSearch', 'Clear search')}
              className="absolute end-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* p-1/-m-1 gives the selects' red focus rings room inside the mobile
            scrollport (overflow-x-auto clips anything drawn outside the box);
            on sm+ the row doesn't scroll, so overflow stays visible. */}
        <div className="flex items-center gap-2 w-full sm:w-auto overflow-x-auto sm:overflow-visible p-1 -m-1">
          <Select value={filterBranch} onValueChange={setFilterBranch}>
            <SelectTrigger className="w-[140px] bg-background h-10 shrink-0">
              <SelectValue placeholder={t('common.branch', 'Branch')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('screens.allBranches', 'All Branches')}</SelectItem>
              {branches.filter(b => enabledIds.includes(b.id)).map(b => (
                <SelectItem key={b.id} value={b.id.toString()}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[120px] bg-background h-10 shrink-0">
              <SelectValue placeholder={t('common.status', 'Status')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('screens.allStatuses', 'All Statuses')}</SelectItem>
              <SelectItem value="online">{t('common.online', 'Online')}</SelectItem>
              <SelectItem value="offline">{t('common.offline', 'Offline')}</SelectItem>
              <SelectItem value="expired">{t('common.expired', 'Expired')}</SelectItem>
              <SelectItem value="idle">{t('common.idle', 'Idle')}</SelectItem>
            </SelectContent>
          </Select>

          <ViewToggle mode={view} onChange={setView} />

          {/* Primary action — moved here now that the inline page header
              (which used to hold it) lives in the TopBar. Requires screens:create;
              hidden for view-only roles (backend also enforces pairing). */}
          {canCreate && (
            <Button onClick={() => navigate('/screens/new')} size="sm" className="gap-2 px-4 shadow-sm h-10 shrink-0 ms-auto sm:ms-0">
              <Plus className="w-4 h-4" /> {t('screens.addScreen', 'Link Screen')}
            </Button>
          )}
          {/* Opens the browser player in a new tab — environment-resolved, so
              a staging dashboard opens the staging player (see lib/webPlayer). */}
          <Button
            variant="outline"
            size="icon"
            className="h-10 w-10 shrink-0"
            onClick={() => window.open(WEB_PLAYER_URL, '_blank', 'noopener,noreferrer')}
            title={t('dashboard.openWebPlayer', 'Open web player ({{host}})', { host: WEB_PLAYER_HOST })}
            aria-label={t('dashboard.openWebPlayer', 'Open web player ({{host}})', { host: WEB_PLAYER_HOST })}
          >
            <Globe className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* ── Screens body — list (table) or grid (cards). With no screens at
          all we swap the whole table/grid for the groups-style empty state. */}
      {noScreens ? (
        <ScreensEmptyState
          onAdd={canCreate ? () => navigate('/screens/new') : undefined}
          addLabel={t('screens.addScreen', 'Link Screen')}
        />
      ) : view === 'list' ? (
      <Card className="bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-muted/40">
              <TableRow>
                <TableHead className="w-12 text-center">#</TableHead>
                <TableHead className="min-w-[200px]">{t('screens.screenDetails', 'Screen details')}</TableHead>
                <TableHead className="min-w-[150px]">{t('screens.branch', 'Branch')}</TableHead>
                <TableHead className="min-w-[180px]">{t('screens.lastUpdate', 'Last update')}</TableHead>
                <TableHead className="text-center">{t('common.active', 'Active')}</TableHead>
                <TableHead className="text-end pe-4">{t('common.actions', 'Actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading
                ? Array.from({ length: 4 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton className="h-4 w-4 mx-auto" /></TableCell>
                      <TableCell className="flex gap-3">
                        <Skeleton className="h-10 w-16 rounded-md" />
                        <div className="space-y-2"><Skeleton className="h-4 w-28" /><Skeleton className="h-3 w-16" /></div>
                      </TableCell>
                      <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-28 rounded-sm" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-10 mx-auto rounded-full" /></TableCell>
                      <TableCell className="text-end"><Skeleton className="h-8 w-8 rounded-md ms-auto" /></TableCell>
                    </TableRow>
                  ))
                : filtered.length === 0
                ? <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-24 text-sm">{t('common.noData', 'No matching screens found.')}</TableCell></TableRow>
                : paged.map((s, idx) => (
                  <TableRow
                    key={s.id}
                    className="group hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() => navigate(`/screens/${s.public_id}`)}
                  >
                    <TableCell className="text-center text-muted-foreground font-mono text-xs">{(curPage - 1) * SCREENS_PAGE_SIZE + idx + 1}</TableCell>
                    
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <ScreenThumbnail orientationType={s.orientation_type} rotationDegrees={s.rotation_degrees} />
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{s.name}</span>
                            <DeviceKindChip kind={s.device_kind} />
                          </div>
                          <span className="text-[10px] text-muted-foreground uppercase">
                            {t(`common.orientation.${s.orientation_type}`, s.orientation_type)}{s.rotation_degrees ? ` · ${s.rotation_degrees}°` : ''}
                          </span>
                        </div>
                      </div>
                    </TableCell>

                    <TableCell>
                      {s.branch_id ? (
                        <div className="flex items-center gap-1.5 text-sm font-medium">
                          <MapPin className="w-3.5 h-3.5 text-primary/70" />
                          {branches.find(b => b.id === s.branch_id)?.name}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>

                    <TableCell>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <RefreshCw className="w-3 h-3" />
                          {formatRelativeSyncTime(s.last_seen ?? s.created_at)}
                        </div>
                        {/* Shared status chip — identical across screens page,
                            details, groups, and admin. */}
                        <ScreenStatusBadge screen={s} />
                      </div>
                    </TableCell>

                    <TableCell className="text-center" onClick={e => e.stopPropagation()}>
                      <div className="flex justify-center">
                        <Switch
                          checked={s.is_active}
                          onCheckedChange={() => toggleActive(s)}
                          className="scale-90 data-[state=checked]:bg-primary"
                        />
                      </div>
                    </TableCell>

                    <TableCell className="text-end pe-4" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 gap-1.5 px-2 text-xs"
                          title={t('screens.moreInfo', 'More info')}
                          onClick={() => setInfoTarget(s)}
                        >
                          <Info className="h-3.5 w-3.5" />
                          {t('screens.info', 'Info')}
                        </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          <DropdownMenuLabel className="text-xs text-muted-foreground">{s.name}</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          {can('screens:edit') && (
                            <DropdownMenuItem onClick={() => navigate(`/screens/${s.public_id}`)} className="cursor-pointer">
                              <Pencil className="me-2 h-4 w-4" />
                              {t('common.edit', 'Edit configuration')}
                            </DropdownMenuItem>
                          )}
                          {can('screens:delete') && (
                            <DropdownMenuItem onClick={() => setDeleteTarget(s)} className="text-destructive cursor-pointer focus:bg-destructive/10">
                              <Trash2 className="me-2 h-4 w-4" />
                              {t('screens.deleteScreen', 'Delete screen')}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          title={t('common.syncStatus', 'Sync status')}
                          aria-label={t('common.syncStatus', 'Sync status')}
                          onClick={() => refreshScreen(s)}
                          disabled={syncingId === s.id}
                        >
                          <RotateCw className={`h-3.5 w-3.5 ${syncingId === s.id ? 'animate-spin' : ''}`} />
                        </Button>
                      </div>
                    </TableCell>

                  </TableRow>
                ))
              }
            </TableBody>
          </Table>
        </div>
      </Card>
      ) : (
        isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-muted-foreground py-24 text-sm">{t('common.noData', 'No matching screens found.')}</div>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {paged.map(s => (
              <ScreenGridCard
                key={s.id}
                screen={s}
                branchName={s.branch_id ? branches.find(b => b.id === s.branch_id)?.name : undefined}
                syncing={syncingId === s.id}
                onOpen={() => navigate(`/screens/${s.public_id}`)}
                onInfo={() => setInfoTarget(s)}
                onSync={() => refreshScreen(s)}
                onToggleActive={() => toggleActive(s)}
                onEdit={() => navigate(`/screens/${s.public_id}`)}
                onDelete={() => setDeleteTarget(s)}
              />
            ))}
          </ul>
        )
      )}

      {/* Pager — shared by both views; shown once there's a loaded, non-empty list. */}
      {!isLoading && filtered.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-muted-foreground tabular-nums">
            {t('dashboard.showingRange', {
              defaultValue: 'Showing {{from}}–{{to}} of {{total}}',
              from: (curPage - 1) * SCREENS_PAGE_SIZE + 1,
              to: Math.min(curPage * SCREENS_PAGE_SIZE, filtered.length),
              total: filtered.length,
            })}
          </p>
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline" size="sm" className="h-7 w-7 p-0"
              disabled={curPage <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              aria-label={t('common.previous', 'Previous')}
            >
              <PrevIcon className="w-4 h-4" />
            </Button>
            <span className="text-[11px] tabular-nums text-muted-foreground px-1 min-w-[3rem] text-center">
              {curPage} / {totalPages}
            </span>
            <Button
              variant="outline" size="sm" className="h-7 w-7 p-0"
              disabled={curPage >= totalPages}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              aria-label={t('common.next', 'Next')}
            >
              <NextIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
      </div>
      )}

      {/* The same warning modal the details page uses. This list is the more
          dangerous surface of the two — every row looks alike, so the realistic
          mistake is deleting the WRONG screen — and it used to have the weaker
          confirmation: a generic dialog that never named the screen. */}
      <DeleteScreenDialog
        open={deleteTarget !== null}
        screenName={deleteTarget?.name ?? ''}
        onClose={() => setDeleteTarget(null)}
        onDeletePermanent={remove}
        hasDevice={deleteTarget?.has_device === true}
        onDetachInstead={() => setDetachTarget(deleteTarget)}
      />

      {detachTarget && (
        <DetachScreenDialog
          open={detachTarget !== null}
          onClose={() => setDetachTarget(null)}
          publicId={detachTarget.public_id}
          screenName={detachTarget.name}
          onDetached={() => {
            setScreens(prev => prev.map(s =>
              s.id === detachTarget.id ? { ...s, has_device: false } : s))
            setDetachTarget(null)
          }}
        />
      )}

      {/* Right-side info drawer — opens from a row/card's "More info" button.
          Loads the fuller screen payload + logs + current content on open. */}
      <ScreenInfoDrawer
        open={infoTarget !== null}
        publicId={infoTarget?.public_id ?? null}
        onClose={() => setInfoTarget(null)}
        onDeleted={() => { if (infoTarget) setDeleteTarget(infoTarget) }}
      />
    </div>
  )
}

// ── ScreenGridCard ─────────────────────────────────────────────────────────
// Card variant of a screen row for the grid view — thumbnail + name/chip,
// branch + last-update, a status badge, an active switch, and sync / edit /
// delete actions mirroring the table row.
function ScreenGridCard({
  screen, branchName, syncing, onOpen, onInfo, onSync, onToggleActive, onEdit, onDelete,
}: {
  screen: Screen
  branchName?: string
  syncing: boolean
  onOpen: () => void
  onInfo: () => void
  onSync: () => void
  onToggleActive: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const stop = (fn: () => void) => (e: React.MouseEvent) => { e.stopPropagation(); e.preventDefault(); fn() }
  return (
    <li
      role="button" tabIndex={0} onClick={onOpen}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onOpen() }}
      className="group rounded-xl border border-[hsl(var(--border-card,var(--border)))] bg-card p-3 hover:border-primary/30 transition-all cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/30"
    >
      <div className="flex items-center gap-3">
        <ScreenThumbnail orientationType={screen.orientation_type} rotationDegrees={screen.rotation_degrees} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold truncate">{screen.name}</span>
            <DeviceKindChip kind={screen.device_kind} />
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground truncate">
            {branchName ? <><MapPin className="w-3 h-3 text-primary/70 shrink-0" />{branchName}</> : <span className="uppercase">{t(`common.orientation.${screen.orientation_type}`, screen.orientation_type)}</span>}
          </div>
        </div>
        <ScreenStatusBadge screen={screen} />
      </div>

      <div className="mt-3 pt-2.5 border-t border-border/60 flex items-center justify-between" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <RefreshCw className="w-3 h-3" />
          {formatRelativeSyncTime(screen.last_seen ?? screen.created_at)}
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-8 gap-1.5 px-2 text-xs" title={t('screens.moreInfo', 'More info')} onClick={stop(onInfo)}>
            <Info className="h-3.5 w-3.5" />
            {t('screens.info', 'Info')}
          </Button>
          <Switch checked={screen.is_active} onCheckedChange={onToggleActive} className="scale-90 data-[state=checked]:bg-primary" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={e => e.stopPropagation()}>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {can('screens:edit') && (
                <DropdownMenuItem onClick={stop(onEdit)} className="cursor-pointer">
                  <Pencil className="me-2 h-4 w-4" />{t('common.edit', 'Edit configuration')}
                </DropdownMenuItem>
              )}
              {can('screens:delete') && (
                <DropdownMenuItem onClick={stop(onDelete)} className="text-destructive cursor-pointer focus:bg-destructive/10">
                  <Trash2 className="me-2 h-4 w-4" />{t('common.delete', 'Unlink display')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="icon" className="h-8 w-8" title={t('common.syncStatus', 'Sync status')} aria-label={t('common.syncStatus', 'Sync status')} onClick={onSync} disabled={syncing}>
            <RotateCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>
    </li>
  )
}

// Summary metric card — soft-tinted icon tile + label / value / hint. Matches
// the Dashboard's StatCard aesthetic so the two pages read as one system.
function StatCard({
  icon: Icon, tile, iconColor, label, value, hint,
}: {
  icon: typeof Monitor
  tile: string
  iconColor: string
  label: string
  value: number
  hint?: string
}) {
  return (
    // Phone: icon above the text, so the half-width card keeps its label
    // whole ("Total Screens", not "Total Sc…").
    <div className="rounded-2xl bg-card p-4 sm:p-5 flex max-sm:flex-col max-sm:items-start items-center gap-3 sm:gap-4">
      <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shrink-0 ${tile}`}>
        <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${iconColor}`} />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium text-muted-foreground max-sm:whitespace-normal truncate">{label}</p>
        <p className="text-2xl font-bold leading-tight tabular-nums">{value}</p>
        {hint && <p className="text-[11px] text-muted-foreground truncate mt-0.5">{hint}</p>}
      </div>
    </div>
  )
}

